/**
 * Safe storage abstraction layer for localStorage and sessionStorage
 * Handles errors gracefully (incognito mode, quota exceeded, etc.)
 * All values are automatically JSON serialized/deserialized
 */
import { createLogger } from './logger'

const logger = createLogger('Storage')

class StorageAdapter {
  constructor(storageType = 'local') {
    this.storageType = storageType
    this.storage = storageType === 'local' ? window.localStorage : window.sessionStorage
    this.available = this.checkAvailability()
    
    if (!this.available) {
      logger.warn(`${storageType}Storage is not available. Using in-memory fallback.`)
      this.memoryStorage = new Map()
    }
  }

  /**
   * Check if storage is available (fails in incognito, disabled cookies, etc.)
   */
  checkAvailability() {
    try {
      const test = '__storage_test__'
      this.storage.setItem(test, test)
      this.storage.removeItem(test)
      return true
    } catch {
      return false
    }
  }

  /**
   * Get item from storage with optional default value
   * 
   * @param {string} key - Storage key
   * @param {any} defaultValue - Value to return if key doesn't exist or parse fails
   * @returns {any} - Parsed value or default
   */
  get(key, defaultValue = null) {
    if (!this.available) {
      return this.memoryStorage.has(key) ? this.memoryStorage.get(key) : defaultValue
    }
    
    try {
      const item = this.storage.getItem(key)
      if (item === null) return defaultValue
      
      // Try to parse as JSON first
      try {
        return JSON.parse(item)
      } catch (parseError) {
        // If parsing fails, it might be a plain string (legacy storage)
        // Return the raw value for backward compatibility
        return item
      }
    } catch (error) {
      logger.error(`Storage.get error for key "${key}"`, error)
      return defaultValue
    }
  }

  /**
   * Set item in storage (auto JSON stringifies)
   * 
   * @param {string} key - Storage key
   * @param {any} value - Value to store (will be JSON stringified)
   * @returns {boolean} - True if successful, false otherwise
   */
  set(key, value) {
    if (!this.available) {
      this.memoryStorage.set(key, value)
      return true
    }
    
    try {
      this.storage.setItem(key, JSON.stringify(value))
      return true
    } catch (error) {
      // Common errors: QuotaExceededError, SecurityError
      logger.error(`Storage.set error for key "${key}"`, error)
      
      // Try to free up space if quota exceeded
      if (error.name === 'QuotaExceededError') {
        logger.warn('Storage quota exceeded. Consider clearing old data.')
      }
      
      return false
    }
  }

  /**
   * Remove item from storage
   * 
   * @param {string} key - Storage key to remove
   */
  remove(key) {
    if (!this.available) {
      this.memoryStorage.delete(key)
      return
    }
    
    try {
      this.storage.removeItem(key)
    } catch (error) {
      logger.error(`Storage.remove error for key "${key}"`, error)
    }
  }

  /**
   * Clear all items from storage
   */
  clear() {
    if (!this.available) {
      this.memoryStorage.clear()
      return
    }
    
    try {
      this.storage.clear()
    } catch (error) {
      logger.error('Storage.clear error', error)
    }
  }

  /**
   * Check if key exists in storage
   * 
   * @param {string} key - Storage key
   * @returns {boolean} - True if key exists
   */
  has(key) {
    if (!this.available) {
      return this.memoryStorage.has(key)
    }
    
    try {
      return this.storage.getItem(key) !== null
    } catch (error) {
      logger.error(`Storage.has error for key "${key}"`, error)
      return false
    }
  }

  /**
   * Get all keys in storage
   * 
   * @returns {string[]} - Array of storage keys
   */
  keys() {
    if (!this.available) {
      return Array.from(this.memoryStorage.keys())
    }
    
    try {
      return Object.keys(this.storage)
    } catch (error) {
      logger.error('Storage.keys error', error)
      return []
    }
  }

  /**
   * Get storage size estimate in bytes
   * 
   * @returns {number} - Approximate size in bytes
   */
  getSize() {
    if (!this.available) {
      let size = 0
      this.memoryStorage.forEach((value) => {
        size += JSON.stringify(value).length
      })
      return size
    }
    
    try {
      let size = 0
      for (let key in this.storage) {
        if (this.storage.hasOwnProperty(key)) {
          size += key.length + this.storage[key].length
        }
      }
      return size
    } catch (error) {
      logger.error('Storage.getSize error', error)
      return 0
    }
  }
}

// Export singleton instances
export const storage = new StorageAdapter('local')
export const sessionStorage = new StorageAdapter('session')

// Export class for testing/custom instances
export { StorageAdapter }
